import { NextFunction, Request, Response } from 'express';
import multer from 'multer';
import { S3Client } from '@aws-sdk/client-s3';
import { aws } from '../config/index';
import { getUploadTypeFromMimeType, validFileTypes } from '../utils/helpers/attachment.helper';
import path from 'path';
import { jsonStatus, status } from '../utils/helpers/api.responses';
import { HttpException } from '../utils/exceptions/httpException';
import { ALLOWED_AUDIO_TYPE, ALLOWED_FILE_TYPES, ALLOWED_IMAGE_TYPE, ALLOWED_VIDEO_TYPE, UPLOAD_TYPES } from '../utils/types/atttachment/attachment.type';
import { AttachmentType } from '../Modules/Attachment/interfaces/attcahment.interface';

const MB = 1024 * 1024;
const MAX_IMAGE_SIZE = 5 * MB;
const MAX_VIDEO_SIZE = 50 * MB;
const MAX_DOCUMENT_SIZE = 50 * MB;
const MAX_REQUEST_SIZE = 80 * MB;

const MAX_SCREENSHOTS = 6;
const MAX_VIDEOS = 4;
const MAX_CERTIFICATES = 20;
const MAX_DOCUMENTS = 20;

export const isS3Enabled = aws.AWS_ACCESS_KEY_ID && aws.AWS_SECRET_ACCESS_KEY;

export const s3 = isS3Enabled
    ? new S3Client({
        credentials: {
            accessKeyId: aws.AWS_ACCESS_KEY_ID,
            secretAccessKey: aws.AWS_SECRET_ACCESS_KEY,
        },
        region: aws.AWS_REGION,
    })
    : null;

export const s3KeyFunction = (req: any, file: Express.Multer.File, cb: (error: any, key?: string) => void) => {
    const { moduleName } = req.query;
    const allowedFolders = [AttachmentType.PROFILE_IMAGE, AttachmentType.CERTIFICATE, AttachmentType.DOCUMENT, AttachmentType.IDENTIFICATION, AttachmentType.VIDEO, AttachmentType.CHATMEDIA];
    const imageType = allowedFolders.includes(moduleName) ? moduleName : AttachmentType.DOCUMENT;

    let typeFolder = 'unknown';
    if (file.fieldname === 'identification') {
        typeFolder = 'identification';
    } else if (file.fieldname === 'videos') {
        typeFolder = 'video';
    } else if (file.fieldname === 'profileImage') {
        typeFolder = 'profile';
    } else if (file.fieldname === 'certificate') {
        typeFolder = 'certificate';
    } else if (file.fieldname === 'document') {
        typeFolder = 'document';
    } else if (file.fieldname === 'chatMedia') {
        typeFolder = 'chatMedia'
    }

    const folder = req.originalUrl.includes('Gard-Labour-Hire') ? 'Gard-Labour-Hire' : req.query.folder || 'Gard-Labour-Hire';
    const uniqueSuffix = Date.now() + '-' + Math.round(Math.random() * 1e9);
    const fileExtension = path.extname(file.originalname);

    const filePath = `${folder}/${imageType}/${typeFolder}/${uniqueSuffix}${fileExtension}`;
    cb(null, filePath);
};

const checkFileSize = (req: Request, res: Response, next: NextFunction) => {
    const contentLength = parseInt(req.headers['content-length'] || '0', 10);
    const t = req.t || ((key: string, options?: any) => key);
    if (contentLength > MAX_REQUEST_SIZE) {
        throw new HttpException(
            status.BadRequest,
            t('Upload.request_too_large', { size: MAX_REQUEST_SIZE / MB })
        );
    }
    next();
};
export const s3MetadataFunction = (file: Express.Multer.File, cb: (error: any, metadata?: any) => void) => {
    try {
        const { originalname } = file;
        const fileExtension = (originalname.match(/\.+[\S]+$/) || [])[0];
        const metadata = {
            fieldName: file.fieldname,
            extension: fileExtension?.slice(1),
            contentLength: file.size.toString(),
        };
        cb(null, metadata);
    } catch (error) {
        cb(error);
    }
};
const configuredMulter = () => {
    return multer({
        storage: multer.memoryStorage(),
        limits: {
            fileSize: MAX_DOCUMENT_SIZE,
            fieldSize: MAX_DOCUMENT_SIZE,
        },
        fileFilter: function (req, file, cb) {
            const t = req.t || ((key: string, options?: any) => key);
            const type = getUploadTypeFromMimeType(file.mimetype);
            const availableTypes = validFileTypes(type);
            if (!type || availableTypes.length === 0) {
                return cb(
                    new HttpException(
                        status.BadRequest,
                        t('Upload.invalid_file_type')
                    )
                );
            }
            const isIdentification = file.fieldname === 'identification';
            const isVideo = file.fieldname === 'videos';
            const isProfileImage = file.fieldname === 'profileImage';
            const isCertificate = file.fieldname === 'certificate';
            const isDocument = file.fieldname === 'document';
            const isChatMedia = file.fieldname === 'chatMedia'


            if (isChatMedia && ![
                ...ALLOWED_IMAGE_TYPE,
                ...ALLOWED_VIDEO_TYPE,
                ...ALLOWED_AUDIO_TYPE,
                ...ALLOWED_FILE_TYPES,
            ].includes(file.mimetype)) {
                return cb(
                    new HttpException(
                        status.BadRequest,
                        t('Upload.invalid_file_type')
                    )
                );
            }


            if (isIdentification && !(ALLOWED_IMAGE_TYPE.includes(file.mimetype) || ALLOWED_FILE_TYPES.includes(file.mimetype))) {
                return cb(  
                    new HttpException(
                        status.BadRequest,
                        t('Upload.invalid_file_type')
                    )
                );
            }
            if (isVideo && !(ALLOWED_VIDEO_TYPE.includes(file.mimetype) || ALLOWED_AUDIO_TYPE.includes(file.mimetype))) {
                return cb(
                    new HttpException(
                        status.BadRequest,
                        t('Upload.invalid_file_type'))
                );
            }
            if (isProfileImage && !(ALLOWED_IMAGE_TYPE.includes(file.mimetype))) {
                return cb(
                    new HttpException(
                        status.BadRequest,
                        t('Upload.invalid_file_type'))
                );
            }
            if (isCertificate && !(ALLOWED_FILE_TYPES.includes(file.mimetype) || ALLOWED_IMAGE_TYPE.includes(file.mimetype))) {
                return cb(
                    new HttpException(
                        status.BadRequest,
                        t('Upload.invalid_file_type'))
                );
            }
            if (isDocument && !(ALLOWED_FILE_TYPES.includes(file.mimetype) || ALLOWED_IMAGE_TYPE.includes(file.mimetype))) {
                return cb(
                    new HttpException(
                        status.BadRequest,
                        t('Upload.invalid_file_type'))
                );
            }

            if (type === UPLOAD_TYPES.IMAGE && file.size > MAX_IMAGE_SIZE) {
                return cb(
                    new HttpException(
                        status.BadRequest,
                        t('Upload.file_too_large', {
                            field: t('Upload.image'),
                            size: MAX_IMAGE_SIZE / MB,
                        })
                    )
                );
            }
            if (type === UPLOAD_TYPES.VIDEO && file.size > MAX_VIDEO_SIZE) {
                return cb(
                    new HttpException(
                        status.BadRequest,
                        t('Upload.file_too_large', {
                            field: t('Upload.video'),
                            size: MAX_VIDEO_SIZE / MB,
                        })
                    )
                );
            }
            if (type === UPLOAD_TYPES.DOCUMENT && file.size > MAX_DOCUMENT_SIZE) {
                return cb(
                    new HttpException(
                        status.BadRequest,
                        t('Upload.file_too_large', {
                            field: t('Upload.document'),
                            size: MAX_DOCUMENT_SIZE / MB,
                        })
                    )
                );
            }

            cb(null, true);
        },
    });
};

export const UploadMultipleFiles = () => {
    return async (req: Request, res: Response, next: NextFunction) => {
        const t = req.t || ((key: string, options?: any) => key);
        checkFileSize(req, res, async (err) => {
            if (err) return next(err);
            configuredMulter().fields([
                { name: 'profileImage', maxCount: 1 },
                { name: 'identification' },
                { name: 'videos' },
                { name: 'certificate' },
                { name: 'chatMedia' }
            ])(req, res, (error: any) => {
                if (error instanceof multer.MulterError) {
                    let message = t('Upload.upload_error');
                    let code = error.code || 'UNKNOWN_ERROR';
                    let details = t('General.no_details');
                    switch (error.code) {
                        case 'LIMIT_UNEXPECTED_FILE':
                            message = t('Upload.unexpected_field', { field: error.field });
                            details = t('Upload.unexpected_field_details', { field: error.field });
                            break;
                        case 'LIMIT_FILE_SIZE':
                            message = t('Upload.file_too_large', { field: t('Upload.file') });
                            details = t('Upload.file_too_large_details', { size: MAX_DOCUMENT_SIZE / MB });
                            break;
                        default:
                            message = t('Upload.multer_error', { error: error.message });
                            break;
                    }
                    return res.status(status.BadRequest).json({
                        status: jsonStatus.BadRequest,
                        code,
                        message,
                        details,
                    });
                } else if (error) {
                    return res.status(status.BadRequest).json({
                        status: jsonStatus.BadRequest,
                        message: error.message ?? t('Upload.upload_error'),
                    });
                }
                if (!req.files || Object.keys(req.files).length === 0) {
                    return res.status(status.BadRequest).json({
                        status: jsonStatus.BadRequest,
                        message: t('Upload.no_file_uploaded'),
                    });
                }
                next();
            });
        });
    };
};